import React from "react";
import { CanonicalBusinessEvent } from "../../../../types/cbes";
import { isCanonicalBusinessEvent } from "../../../../helpers/typeChecks";
import {
  AccountingEventsGroup,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../../../../types";
import { Stage } from "../../../../lusca-search-api/generated-src";

type JsonDataType =
  | CanonicalBusinessEvent
  | AccountingEventsGroup
  | DigitalBusinessDocument
  | DigitalCostDocument
  | DigitalPaymentDocument;

interface UsecaseStateResult {
  shouldShowUsecaseDropdown: boolean;
  dropdownDisabled: boolean;
  shouldShowDiff: boolean;
}

export const useUsecaseState = (
  filteredData: unknown[],
  crossStageData: unknown[],
  crossStageEnabled: boolean,
  compareStage: Stage,
  currentStage: Stage,
  crossStageLoading: boolean
): UsecaseStateResult => {
  return React.useMemo(() => {
    const isCbe =
      filteredData.length > 0 &&
      isCanonicalBusinessEvent(filteredData[0] as JsonDataType);

    if (!isCbe) {
      return {
        shouldShowUsecaseDropdown: false,
        dropdownDisabled: false,
        shouldShowDiff: true,
      };
    }

    const isLoadingCrossStage =
      crossStageEnabled && compareStage !== currentStage && crossStageLoading;
    const hasCrossStageData =
      crossStageEnabled &&
      compareStage !== currentStage &&
      crossStageData.length > 0;

    let hasCommonUsecases = true;
    if (crossStageEnabled && compareStage !== currentStage && isCbe) {
      const currentUsecases = new Set(
        filteredData
          .filter((item): item is CanonicalBusinessEvent =>
            isCanonicalBusinessEvent(item as JsonDataType)
          )
          .map((cbe) => cbe.usecaseName)
      );
      const compareUsecases = new Set(
        crossStageData
          .filter((item): item is CanonicalBusinessEvent =>
            isCanonicalBusinessEvent(item as JsonDataType)
          )
          .map((cbe) => cbe.usecaseName)
      );
      hasCommonUsecases = [...currentUsecases].some((uc) =>
        compareUsecases.has(uc)
      );
    }

    const shouldShowDiff =
      !isLoadingCrossStage &&
      (!crossStageEnabled ||
        compareStage === currentStage ||
        hasCrossStageData) &&
      hasCommonUsecases;

    return {
      shouldShowUsecaseDropdown: true,
      dropdownDisabled:
        crossStageEnabled &&
        compareStage !== currentStage &&
        !hasCommonUsecases,
      shouldShowDiff,
    };
  }, [
    filteredData,
    crossStageData,
    crossStageEnabled,
    compareStage,
    currentStage,
    crossStageLoading,
  ]);
};
